import * as React from 'react';
import { useNavigate } from 'react-router';
import { useNotification } from './CustomNotification';
import { createOne as createLeaveRequest, type CreateLeaveRequestPayload } from '../data/leaveRequests';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import Box from '@mui/material/Box';
import RtlButton from './RtlButton';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import DateRangeFilter from './DateRangeFilter';
import type { LeaveRequestType } from '../data/leaveRequests';

interface LeaveRequestFormState {
  values: {
    type: LeaveRequestType | '';
    start_date: string;
    end_date: string;
    note: string;
  };
  errors: Partial<Record<keyof LeaveRequestFormState['values'], string>>;
}

const INITIAL_FORM_VALUES: LeaveRequestFormState['values'] = {
  type: '',
  start_date: '',
  end_date: '',
  note: '',
};

export default function LeaveRequestCreate() {
  const navigate = useNavigate();
  const notifications = useNotification();
  const { t, direction, language } = useLanguage();
  const isRtl = direction === 'rtl';

  // Set dayjs locale based on current language
  React.useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  const [formState, setFormState] = React.useState<LeaveRequestFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const [calculatedDays, setCalculatedDays] = React.useState<number>(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Calculate days when dates change
  React.useEffect(() => {
    if (startDate && endDate) {
      if (endDate.isBefore(startDate)) {
        setCalculatedDays(0);
        setFormState((prev) => ({
          ...prev,
          values: {
            ...prev.values,
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
          },
        }));
      } else {
        const days = endDate.diff(startDate, 'day') + 1; // +1 to include both start and end dates
        setCalculatedDays(days);
        setFormState((prev) => ({
          ...prev,
          values: {
            ...prev.values,
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
          },
        }));
      }
    } else if (startDate) {
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          start_date: startDate.format('YYYY-MM-DD'),
        },
      }));
    } else if (endDate) {
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          end_date: endDate.format('YYYY-MM-DD'),
        },
      }));
    } else {
      setCalculatedDays(0);
      setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          start_date: '',
          end_date: '',
        },
      }));
    }
  }, [startDate, endDate]);

  const setFormValues = React.useCallback(
    (newFormValues: Partial<LeaveRequestFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, ...newFormValues },
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<LeaveRequestFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const validate = React.useCallback((): boolean => {
    const errors: Partial<Record<keyof LeaveRequestFormState['values'], string>> = {};

    if (!formValues.type) {
      errors.type = t('validation.required');
    }

    if (!formValues.start_date) {
      errors.start_date = t('validation.required');
    }

    if (!formValues.end_date) {
      errors.end_date = t('validation.required');
    }

    if (formValues.start_date && formValues.end_date) {
      const start = dayjs(formValues.start_date);
      const end = dayjs(formValues.end_date);
      if (end.isBefore(start)) {
        errors.end_date = t('leaveRequests.endDateBeforeStartDate');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formValues, t, setFormErrors]);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!validate() || isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      try {
        const payload: CreateLeaveRequestPayload = {
          type: formValues.type as LeaveRequestType,
          start_date: formValues.start_date,
          end_date: formValues.end_date,
          note: formValues.note || undefined,
        };

        await createLeaveRequest(payload);
        notifications.show(t('messages.createSuccess'), {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/leave-requests');
      } catch (createError) {
        notifications.show(
          `${t('messages.errorOccurred')}: ${(createError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, navigate, notifications, t, validate, isSubmitting],
  );

  const handleReset = React.useCallback(() => {
    setFormState({
      values: INITIAL_FORM_VALUES,
      errors: {},
    });
    setStartDate(null);
    setEndDate(null);
    setCalculatedDays(0);
  }, []);

  return (
    <PageContainer
      breadcrumbs={[
        { title: t('leaveRequests.title'), path: '/leave-requests' },
        { title: t('leaveRequests.create') },
      ]}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <FormGroup>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 12 }}>
              <DateRangeFilter
                  dateFrom={formValues.start_date || null}
                  dateTo={formValues.end_date || null}
                  onDateFromChange={(date) => setStartDate(date ? dayjs(date) : null)}
                  onDateToChange={(date) => setEndDate(date ? dayjs(date) : null)}
              />
              {(formErrors.start_date || formErrors.end_date) && (
                  <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        mt: 0.5,
                        display: 'block',
                        ...(isRtl ? { mr: 1.75, textAlign: 'right' } : { ml: 1.75, textAlign: 'left' }),
                      }}
                  >
                    {formErrors.start_date || formErrors.end_date}
                  </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!formErrors.type}>
                <InputLabel
                    sx={{
                      transformOrigin: isRtl ? 'top right' : 'top left',
                      right: isRtl ? 0 : 'auto',
                      left: isRtl ? 'auto' : 0,
                    }}
                >
                  {t('leaveRequests.type')}
                </InputLabel>
                <Select
                    value={formValues.type}
                    label={t('leaveRequests.type')}
                    onChange={(e) =>
                        setFormValues({ type: e.target.value as LeaveRequestType | '' })
                    }
                >
                  <MenuItem value="normal">{t('leaveRequests.normal')}</MenuItem>
                  <MenuItem value="sick">{t('leaveRequests.sick')}</MenuItem>
                </Select>
                {formErrors.type && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          mt: 0.5,
                          ...(isRtl ? { mr: 1.75, textAlign: 'right' } : { ml: 1.75, textAlign: 'left' }),
                        }}
                    >
                      {formErrors.type}
                    </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('leaveRequests.calculatedDays')}
                value={calculatedDays}
                InputLabelProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    right: isRtl ? 0 : 'auto',
                    left: isRtl ? 'auto' : 0,
                    transformOrigin: isRtl ? 'top right' : 'top left',
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
                helperText={t('leaveRequests.calculatedDaysHelper')}
                FormHelperTextProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    marginRight: isRtl ? '14px' : 0,
                    marginLeft: isRtl ? 0 : '14px',
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={t('leaveRequests.note')}
                value={formValues.note}
                onChange={(e) => setFormValues({ note: e.target.value })}
                helperText={t('common.optional')}
                InputLabelProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    right: isRtl ? 0 : 'auto',
                    left: isRtl ? 'auto' : 0,
                    transformOrigin: isRtl ? 'top right' : 'top left',
                  },
                }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: isRtl ? 'right' : 'left',
                    marginX: '14px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </FormGroup>

        <Divider />

        <Stack
          direction="row"
          justifyContent={isRtl ? 'flex-end' : 'flex-start'}
          sx={{ columnGap: isRtl ? 2 : 1 }}
        >
          <RtlButton 
            variant="outlined" 
            onClick={handleReset}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </RtlButton>
          <RtlButton
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              background: '#028bcb !important',
              boxShadow: 'unset !important',
              border: '0 !important',
              '&:hover': { background: '#0277a8 !important' },
              '&:disabled': {
                background: '#ccc !important',
                color: '#666 !important',
              },
            }}
          >
            {isSubmitting ? t('common.loading') : t('common.create')}
          </RtlButton>
        </Stack>
      </Box>
    </PageContainer>
  );
}

